import React from "react"
import { graphql } from "gatsby"
import { HelmetDatoCms } from "gatsby-source-datocms"
import {
  Intro,
  Cards,
  Card,
  Block,
  Container,
  VideoEmbed,
  Transcript
} from "@components"
import Layout from "@layouts/layout"
import { JsonLd } from "react-schemaorg"
import PropTypes from "prop-types"

const VideoPage = ({ data, pageContext }) => {
  const {
    title,
    seoMetaTags,
    video,
    videoTitle,
    descriptionNode,
    transcriptNode,
    transcript,
    duration,
    pathways,
    showPathways,
    meta
  } = data.videoPage

  const allPathways = [...pathways, ...data.globalConfig.defaultPathways].slice(
    0,
    3
  )

  const introConfig = {
    breadcrumbs: pageContext.breadcrumbs,
    title,
    lead: descriptionNode?.childMarkdownRemark?.html
  }

  const schema = {
    "@context": "https://schema.org",
    "@type": "VideoObject",
    name: videoTitle || title,
    embedUrl: video?.url,
    thumbnailUrl: video?.thumbnailUrl,
    width: video?.width,
    height: video?.height
  }

  if (meta?.createdAt) schema.uploadDate = meta.createdAt
  if (duration) schema.duration = `PT${duration}`
  if (transcript) schema.transcript = transcript

  return (
    <Layout>
      <HelmetDatoCms defer={false} seo={seoMetaTags} />
      <Block gutters>
        <Container>
          <Intro {...introConfig} />
        </Container>
      </Block>
      <Block gutters padding="bottom">
        <Container>
          <VideoEmbed video={video} title={videoTitle || title} />
        </Container>
      </Block>
      {transcriptNode?.childMarkdownRemark?.html && (
        <Block gutters padding="bottom">
          <Container>
            <Transcript
              html={transcriptNode.childMarkdownRemark.html}
              id={`video-transcript`}
              heading={`video transcript`}
            />
          </Container>
        </Block>
      )}
      {showPathways && (
        <Block padding="double" gutters>
          <Cards>
            {allPathways.map(pathway => {
              const { link, displayText, url } = pathway.link
              return (
                <Card
                  key={`pathway-${pathway.id}`}
                  {...pathway}
                  link={{
                    path: link && link.path,
                    title: displayText,
                    url: url && url
                  }}
                  pathway
                  elevated
                  truncate={3}
                />
              )
            })}
          </Cards>
        </Block>
      )}
      <JsonLd item={schema} />
    </Layout>
  )
}

VideoPage.propTypes = {
  data: PropTypes.object.isRequired,
  pageContext: PropTypes.object
}

export default VideoPage

export const query = graphql`
  query videoPageById($id: String!, $language: String!) {
    videoPage: datoCmsVideoPage(locale: $language, id: { eq: $id }) {
      id
      title
      slug
      videoTitle
      duration
      transcript
      descriptionNode {
        childMarkdownRemark {
          html
        }
      }
      transcriptNode {
        childMarkdownRemark {
          html
        }
      }
      video {
        url
        title
        provider
        providerUid
        thumbnailUrl
        width
        height
      }
      pathways {
        ...Card
      }
      showPathways
      seoMetaTags {
        ...GatsbyDatoCmsSeoMetaTags
      }
      meta {
        createdAt
      }
    }

    globalConfig: datoCmsGlobal(locales: { eq: $language }) {
      defaultPathways {
        ...Card
      }
    }
  }
`
